import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark" | "system";
export type Density = "comfortable" | "compact";

const STORAGE_KEY = "cf-pm.theme";
const ACCENT_KEY = "cf-pm.accent";
const DENSITY_KEY = "cf-pm.density";
const REDUCED_MOTION_KEY = "cf-pm.reducedMotion";

// localStorage access can throw `SecurityError` in private/incognito mode
// on some browsers. Wrap reads/writes so a throw here can never crash the
// app at boot. Theme just stops persisting; the `system` default still
// applies and matchMedia still works.
function safeRead(): Theme | null {
  try {
    return localStorage.getItem(STORAGE_KEY) as Theme | null;
  } catch {
    return null;
  }
}
function safeWrite(value: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore — private mode or quota */
  }
}

export function getTheme(): Theme {
  return safeRead() ?? "system";
}

export function setTheme(theme: Theme) {
  safeWrite(theme);
  applyTheme(theme);
  // Notify any useTheme() subscribers in the same tab.
  window.dispatchEvent(new CustomEvent("cf-pm:theme"));
}

export function applyTheme(theme: Theme = getTheme()) {
  const root = document.documentElement;
  const resolved =
    theme === "system"
      ? matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  if (resolved === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

/** Returns the currently-active resolved theme (light or dark, never system). */
export function getResolvedTheme(): "light" | "dark" {
  const t = getTheme();
  if (t !== "system") return t;
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function subscribe(callback: () => void) {
  window.addEventListener("cf-pm:theme", callback);
  const mq = matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener("cf-pm:theme", callback);
    mq.removeEventListener("change", callback);
    window.removeEventListener("storage", callback);
  };
}

// ---------------------------------------------------------------------------
// Accent color
// ---------------------------------------------------------------------------

// One source of truth for the accent palette — the settings page renders
// from this list. `base` overrides `--primary`; `hover` overrides
// `--primary-hover` (hand-darkened ~10% per swatch). The default green has
// no overrides: removing the inline properties lets because-ui.css rule,
// including its dark-theme adjustments.
export const ACCENT_OPTIONS: {
  value: string;
  label: string;
  base: string;
  hover: string;
}[] = [
  { value: "green", label: "Green", base: "#1f8a5b", hover: "#157049" },
  { value: "orange", label: "Orange", base: "#e8612c", hover: "#cf4e1e" },
  { value: "blue", label: "Blue", base: "#3b82d4", hover: "#2f6fb8" },
  { value: "violet", label: "Violet", base: "#8b5cf6", hover: "#7a46e0" },
];

export function getAccent(): string {
  try {
    return localStorage.getItem(ACCENT_KEY) ?? "green";
  } catch {
    return "green";
  }
}

export function setAccent(value: string): void {
  try {
    localStorage.setItem(ACCENT_KEY, value);
  } catch {
    /* ignore */
  }
}

/**
 * Applies an accent by KEY (e.g. "violet") to the tokens the stylesheet
 * actually consumes. The previous implementation set an `--accent` variable
 * no CSS rule read — the setting was a placebo (T47).
 */
export function applyAccent(key: string): void {
  const root = document.documentElement;
  const opt = ACCENT_OPTIONS.find((a) => a.value === key);
  if (!opt || opt.value === "green") {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-hover");
    return;
  }
  root.style.setProperty("--primary", opt.base);
  root.style.setProperty("--primary-hover", opt.hover);
}

// ---------------------------------------------------------------------------
// Density
// ---------------------------------------------------------------------------

export function getDensity(): Density {
  try {
    return (
      (localStorage.getItem(DENSITY_KEY) as Density | null) ?? "comfortable"
    );
  } catch {
    return "comfortable";
  }
}

export function setDensity(value: Density): void {
  try {
    localStorage.setItem(DENSITY_KEY, value);
  } catch {
    /* ignore */
  }
}

export function applyDensity(value: Density): void {
  document.documentElement.setAttribute("data-density", value);
}

// ---------------------------------------------------------------------------
// Reduced motion
// ---------------------------------------------------------------------------

export function getReducedMotion(): boolean {
  try {
    return localStorage.getItem(REDUCED_MOTION_KEY) === "true";
  } catch {
    return false;
  }
}

export function setReducedMotion(value: boolean): void {
  try {
    localStorage.setItem(REDUCED_MOTION_KEY, String(value));
  } catch {
    /* ignore */
  }
}

export function applyReducedMotion(value: boolean): void {
  if (value) {
    document.documentElement.setAttribute("data-reduced-motion", "true");
  } else {
    document.documentElement.removeAttribute("data-reduced-motion");
  }
}

// ---------------------------------------------------------------------------
// Apply all persisted appearance settings (call at bootstrap)
// ---------------------------------------------------------------------------

export function applyAppearance(): void {
  applyAccent(getAccent());
  applyDensity(getDensity());
  applyReducedMotion(getReducedMotion());
}

/**
 * React hook for reading + toggling the active theme.
 * `theme` is the resolved value (light / dark); `preference` is what's
 * stored (light / dark / system). `toggle()` flips between explicit
 * light and dark.
 */
export function useTheme() {
  const preference = useSyncExternalStore(
    subscribe,
    getTheme,
    () => "system" as const,
  );
  const resolved = useSyncExternalStore(
    subscribe,
    getResolvedTheme,
    () => "light" as const,
  );
  return {
    preference,
    theme: resolved,
    set: setTheme,
    toggle: () => setTheme(resolved === "dark" ? "light" : "dark"),
  };
}
